export type HeroClass = "WARRIOR" | "ARCHER" | "MAGE";
/** Legacy DB values on gear templates — treated as ARCHER / MAGE in UI. */
export type LegacyHeroClass = "RANGER" | "PRIEST";
export type ItemHeroClass = HeroClass | LegacyHeroClass;
export type HeroGender = "m" | "w";
export type Formation = "FRONT" | "MID" | "BACK";
export type BattleType = "PVE" | "PVP" | "ARENA" | "ARENA_SOLO" | "ARENA_PARTY" | "CAMPAIGN" | "WORLD_BOSS" | "CATACOMBS";
export type BattleStatus = "PENDING" | "IN_PROGRESS" | "COMPLETED" | "CLAIMED";
export type BattleResult = "WIN" | "LOSS" | "DRAW";
export type DungeonType = "SOLO" | "MULTIPLAYER";
export type DungeonStatus = "ACTIVE" | "COMPLETED" | "FAILED";
export type SkillName =
  | "BASIC"
  | "SKILL_1"
  | "SKILL_2"
  | "ULT"
  | "SKILL_4"
  | "SKILL_5"
  | "SKILL_6"
  | "SKILL_7"
  | "SKILL_8";

export type HapticImpactStyle = "light" | "medium" | "heavy" | "rigid" | "soft";

export interface SkillHapticConfig {
  enabled: boolean;
  pattern: "impact" | "charge_release" | "continuous_charge" | string;
  impact?: HapticImpactStyle;
  /** Rapid light taps during charge (Telegram has no true continuous haptic). */
  charge_duration_ms?: number;
  charge_interval_ms?: number;
  charge_impact?: HapticImpactStyle;
  charge_impacts?: HapticImpactStyle[];
  charge_delay_ms?: number[];
  release_impact?: HapticImpactStyle;
}

export interface OnPlayerHitHapticConfig {
  enabled: boolean;
  impact: HapticImpactStyle;
}

export interface CombatHapticsConfig {
  skills: Record<string, SkillHapticConfig>;
  on_player_hit?: OnPlayerHitHapticConfig;
}

export interface BuffDefinition {
  name: string;
  description: string;
  icon: string;
  kind: "buff" | "debuff" | string;
  scope: string;
}

export interface ClientConfig {
  combat_haptics: CombatHapticsConfig;
  buff_definitions: Record<string, BuffDefinition>;
}

export interface ActiveBuff {
  buff_id: string;
  tier: number;
  expires_at: string;
  name: string;
  description: string;
  icon: string;
  kind: string;
  stat?: string;
  pct?: number;
}

export interface StatusEffect {
  id: string;
  stacks: number;
  turns_left: number;
  tier: number;
  persistent: boolean;
  kind: "buff" | "debuff" | string;
  dot_source_damage?: number;
}

export interface User {
  id: number;
  telegram_id: number;
  username: string | null;
  first_name: string | null;
  is_admin: boolean;
  created_at: string;
}

export interface HeroStats {
  power: number;
  armor: number;
  endurance: number;
  hp: number;
  patk: number;
  matk: number;
  pdef: number;
  mdef: number;
  crit: number;
}

export interface LevelUpInfo {
  oldLevel: number;
  newLevel: number;
  levelsGained: number;
  skillPointsGained: number;
  statDeltas: HeroStats;
  newStats: HeroStats;
  heroClass: HeroClass;
}

export interface Hero {
  id: number;
  user_id: number;
  level: number;
  xp: number;
  xp_to_next: number;
  xp_into_level: number;
  xp_for_next_level: number;
  hero_class: HeroClass;
  gender: HeroGender;
  name: string;
  formation: Formation;
  stats: HeroStats;
  battle_rating: number;
  rotation: SkillName[];
  rotation_slots: number;
  rotation_slots_max: number;
  rotation_slot_upgrade_cost_diamonds: number;
  rotation_slot_upgrade_increment: number;
  skill_levels: Record<string, number>;
  skill_points: number;
  active_skill_branch?: number;
  skill_branch_unlocked?: boolean;
  active_buffs?: ActiveBuff[];
  energy: number;
  max_energy: number;
  battle_hp: number;
  battle_mana: number;
  max_mana: number;
  energy_costs: Record<string, number>;
  /** Hours left on free energy for new accounts; null when expired. */
  newcomer_energy_bonus_hours_remaining?: number | null;
  auto_battle: AutoBattlePrefs;
  premium?: PremiumStatus | null;
  guild_name?: string | null;
  created_at: string;
}

export interface AutoBattlePrefs {
  mode: AutoBattleMode;
  prompt_on_start: boolean;
  tactical_skills: string[] | null;
  mode_entitlements?: AutoBattleModeEntitlements;
  mode_labels?: Record<string, string>;
}

export interface AutoBattleModeEntitlements {
  tactician: boolean;
  strategist: boolean;
}

export interface PremiumStatus {
  vip: {
    level: number;
    exp: number;
    exp_to_next_level: number;
    subscription_active: boolean;
    subscription_expires_at: string | null;
    xp_bonus_pct: number;
    max_energy: number;
    /** Bonuses at current VIP tier (shown even when subscription inactive). */
    tier_xp_bonus_pct: number;
    tier_max_energy: number;
    /** Energy cap while VIP subscription is active (base 200 → up to this). */
    subscription_max_energy: number;
    free_arena_skip: boolean;
    free_catacombs_skip: boolean;
    gem_auto_craft_unlock: boolean;
    auto_collect_rewards: boolean;
    astral_auto_gather_unlock: boolean;
    weekly_chest_available: boolean;
  };
  pro: {
    level: number;
    exp: number;
    exp_to_next_level: number;
    contract_active: boolean;
    contract_expires_at: string | null;
    stat_bonus_pct: number;
    farm_bonus_pct: number;
  };
  auto_battle: AutoBattleModeEntitlements & { mode_labels: Record<string, string> };
  subscription_cost_diamonds: number;
  subscription_days: number;
  pro_contract_cost_diamonds: number;
  pro_contract_days: number;
}

export type AutoBattleMode = "rotation" | "assist" | "tactician" | "strategist";

export interface BattleUnitInit {
  uid: string;
  name: string;
  team: 0 | 1;
  hero_class: HeroClass;
  formation: Formation;
  hp: number;
  max_hp: number;
  patk: number;
  matk: number;
  pdef: number;
  mdef: number;
  crit?: number;
  spd?: number;
  mana: number;
  effects?: StatusEffect[];
  display_name?: string;
  gender?: HeroGender;
  is_player?: boolean;
  /** e.g. /assets/monsters/grunt_magma.png */
  portrait_path?: string | null;
  /** monster_templates.code for PvE enemies */
  monster_code?: string | null;
  /** Campaign PvE: atk/def_ from map payload (not flat-split patk/pdef) */
  atk?: number;
  def_?: number;
}

export interface BattleUnitSnapshot {
  uid: string;
  hp: number;
  mana: number;
  alive: boolean;
  effects?: StatusEffect[];
  skill_cooldown_remaining?: Record<string, number>;
}

export interface BattleHitEvent {
  target: string;
  damage?: number;
  value?: number;
  hp: number;
  is_crit?: boolean;
  damage_type?: DamageType;
  removed?: string[];
  skill_damage?: number;
  pre_defense_raw?: number;
  defense_mitigated?: number;
  defense_type?: "pdef" | "mdef";
  defense_stat?: number;
}

export type DamageType = "physical" | "magical";

export interface BattleLogEvent {
  turn: number;
  actor: string;
  skill: string;
  skill_display?: string;
  action:
    | "ATTACK"
    | "HEAL"
    | "AOE"
    | "INIT"
    | "JOIN"
    | "DOT_TICK"
    | "BUFF_APPLY"
    | "BUFF_EXPIRE"
    | "BUFF_REMOVE"
    | "STUN_SKIP";
  mass?: boolean;
  multi_hit?: boolean;
  target?: string;
  damage?: number;
  target_hp?: number;
  value?: number;
  is_crit?: boolean;
  damage_type?: DamageType;
  skill_damage?: number;
  pre_defense_raw?: number;
  defense_mitigated?: number;
  defense_type?: "pdef" | "mdef";
  defense_stat?: number;
  effect_id?: string;
  stacks?: number;
  turns_left?: number;
  kind?: string;
  hits?: BattleHitEvent[];
  snapshots?: Record<string, BattleUnitSnapshot>;
  units?: BattleUnitInit[];
}

export interface Battle {
  id: number;
  battle_type: BattleType;
  seed: number;
  status: BattleStatus;
  result: BattleResult | null;
  duration: number;
  start_time: string;
  log: BattleLogEvent[] | null;
  rewards: {
    gold: number;
    xp: number;
    diamonds: number;
    opponent_name?: string;
    opponent_class?: HeroClass;
    opponent_gender?: HeroGender;
  } | null;
  info_messages?: string[];
}

export interface BattleRewards {
  gold: number;
  xp: number;
  diamonds: number;
  arcane_dust?: number;
}

export interface IdleStatus {
  offline_seconds: number;
  accrued_seconds: number;
  max_seconds: number;
  fill_pct: number;
  is_capped: boolean;
}

export interface Balance {
  gold: number;
  diamonds: number;
  star_dust: number;
  arcane_dust: number;
  crypto: number;
  amethyst?: number;
  insignia?: number;
  league_points?: number;
  idle_gold_pending: number;
  idle: IdleStatus;
}

export interface DailyRewardPreview {
  day: number;
  gold: number;
  diamonds: number;
  consumable_item_id: string | null;
  consumable_quantity: number;
  item_template_name: string | null;
  description: string | null;
}

export interface NewbieBonusPreview {
  gold: number;
  diamonds: number;
}

export interface DailyRewardStatus {
  can_claim: boolean;
  streak_day: number;
  max_day: number;
  streak_broken: boolean;
  already_claimed_today: boolean;
  offline_days_missed: number;
  today_reward: DailyRewardPreview;
  upcoming_rewards: DailyRewardPreview[];
  newbie_bonus_today?: NewbieBonusPreview | null;
  newbie_days_remaining?: number;
}

export interface DailyRewardClaimResult {
  claimed_day: number;
  gold: number;
  diamonds: number;
  consumable_item_id: string | null;
  consumable_quantity: number;
  item_template_name: string | null;
  new_streak_day: number;
  newbie_bonus_gold?: number;
  newbie_bonus_diamonds?: number;
  status: DailyRewardStatus;
}

export interface DungeonNode {
  index: number;
  node_type: "battle" | "loot" | "boss";
  cleared: boolean;
  rewards?: { gold: number; xp: number } | null;
}

export interface DungeonRun {
  id: number;
  dungeon_type: DungeonType;
  status: DungeonStatus;
  current_node: number;
  nodes: DungeonNode[];
  started_at: string;
  completed_at: string | null;
  info_messages?: string[];
}

export interface DungeonMoveResult {
  dungeon_run_id: number;
  node_index: number;
  node_type: string;
  battle_result: "WIN" | "LOSS" | null;
  rewards: { gold: number; xp: number } | null;
  dungeon_completed: boolean;
  log: BattleLogEvent[] | null;
  dropped_items?: PlayerItem[];
  info_messages?: string[];
}

export const RENAME_SCROLL_ITEM_ID = "rename_scroll";
export const RENAME_SCROLL_DIAMOND_COST = 50;

export interface ShopItem {
  item_id: string;
  name: string;
  cost_gold: number;
  cost_diamonds: number;
  description: string;
}

export interface InventoryItem {
  id: number;
  item_id: string;
  quantity: number;
  meta: Record<string, unknown>;
  acquired_at: string;
}

export interface ChatChannel {
  code: string;
  name: string;
  sort_order: number;
}

export interface ChatMessage {
  id: number;
  channel_code: string;
  channel_name: string;
  user_id: number;
  display_name: string;
  text: string;
  created_at: string;
}

export type Screen = "main" | "battle" | "top" | "hero" | "shop" | "premium" | "campaign" | "campaignRun" | "skills" | "inventory" | "astral" | "duels" | "worldBoss" | "catacombs" | "blacksmith" | "mpDungeons" | "mpDungeonRun" | "mine" | "guild" | "guildTree" | "arena" | "roulette" | "mounts" | "admin" | "adminCharacter" | "adminGrants" | "debug";

export type AstralRarity = "gray" | "green" | "blue" | "purple" | "orange" | "red";

export interface PlayerAstral {
  id: number;
  template_code: string;
  name: string;
  rarity: AstralRarity;
  stat_group: string | null;
  description: string;
  level: number;
  max_level: number;
  stats: Record<string, number>;
  per_level: Record<string, number>;
  equippable: boolean;
  sell_gold: number;
  sell_star_dust: number;
  bag_slot: number | null;
  equipped_slot: number | null;
  can_upgrade: boolean;
  upgrade_star_dust_cost: number | null;
  upgrade_gold_cost: number | null;
  acquired_at: string;
}

export interface AstralInventorySlot {
  slot: number;
  astral: PlayerAstral | null;
}

export interface EquippedAstralSlot {
  slot: number;
  astral: PlayerAstral | null;
}

export interface GatherTier {
  tier: number;
  name: string;
  cost: number;
  star_dust: number;
}

export interface QuickSellSettings {
  rarities: AstralRarity[];
  remember: boolean;
}

export interface AstralShopItem {
  template_code: string;
  name: string;
  rarity: AstralRarity;
  description: string;
  star_dust_cost: number;
  stats_preview: Record<string, number>;
}

export interface AstralShop {
  unlocked: boolean;
  unlock_level: number;
  star_dust: number;
  inventory_used: number;
  inventory_capacity: number;
  items: AstralShopItem[];
  prices_by_rarity: Record<string, number>;
  rarity_colors: Record<string, string>;
}

export interface AstralShopPurchaseResult {
  ok: boolean;
  message: string;
  star_dust: number;
  purchased_astral: PlayerAstral;
}

export interface AstralState {
  unlocked: boolean;
  unlock_level: number;
  min_gold_balance: number;
  gold: number;
  star_dust: number;
  unlocked_tier: number;
  tiers: GatherTier[];
  inventory_slots: AstralInventorySlot[];
  inventory_used: number;
  inventory_capacity: number;
  equipped_slots: EquippedAstralSlot[];
  equip_slot_count: number;
  total_value: number;
  templates: Record<string, unknown>;
  rarity_colors: Record<string, string>;
  quick_sell_settings: QuickSellSettings;
}

export interface GatherResult {
  tier: number;
  outcome: string;
  template_code: string | null;
  chain_advanced: boolean;
  chain_reset: boolean;
  chain_reset_chance?: number;
  gold_spent: number;
  star_dust_gained: number;
  gathered_astral: PlayerAstral | null;
}

export interface AstralGatherResponse extends AstralState {
  gather_result: GatherResult;
}

export interface AutoGatherSummary {
  gold_budget: number;
  gold_spent: number;
  gather_count: number;
  astrals_gained: number;
  star_dust_gained: number;
  sell_rarities: AstralRarity[];
  astrals_sold: number;
  sell_gold_gained: number;
  sell_star_dust_gained: number;
  stopped_early_bag_full: boolean;
}

export interface AstralAutoGatherResponse extends AstralState {
  auto_gather_summary: AutoGatherSummary;
}

export interface LeaderboardEntry {
  rank: number;
  user_id: number;
  display_name: string;
  hero_class: HeroClass;
  gender: HeroGender;
  level: number;
  battle_rating: number;
  guild_name?: string | null;
  vip_subscription_active?: boolean;
  pro_active?: boolean;
}

export interface LeaderboardResponse {
  entries: LeaderboardEntry[];
  total: number;
  my_rank: number | null;
}

export interface PlayerProfile {
  user_id: number;
  display_name: string;
  hero_class: HeroClass;
  gender: HeroGender;
  level: number;
  formation: Formation;
  battle_rating: number;
  rank: number | null;
  stats: HeroStats;
  guild_name?: string | null;
  vip_subscription_active?: boolean;
  pro_active?: boolean;
}

export type ItemSlot =
  | "HELM"
  | "HANDS"
  | "BODY"
  | "WEAPON"
  | "NECK_R"
  | "NECK_L"
  | "RING_R"
  | "RING_L";

export type ItemRarity = "JUNK" | "COMMON" | "RARE" | "EPIC" | "LEGENDARY";

export interface ItemStats {
  hp?: number;
  atk?: number;
  def_?: number;
  crit?: number;
  spd?: number;
  patk?: number;
  matk?: number;
  pdef?: number;
  mdef?: number;
}

export interface ItemTemplateInfo {
  code: string;
  name: string;
  description: string | null;
  slot: ItemSlot;
  hero_class: ItemHeroClass | null;
  item_level: number;
  rarity: ItemRarity;
  max_sockets: number;
  icon: string;
}

export interface ItemSetInfo {
  set_level: number;
  pieces_equipped: number;
  two_piece_hp: number;
  four_piece_mana_per_action: number;
  two_piece_active: boolean;
  four_piece_active: boolean;
}

export interface PlayerItem {
  id: number;
  template: ItemTemplateInfo;
  enhance_level: number;
  max_enhance: number;
  sockets_unlocked: number;
  socket_gems?: (SocketGem | null)[];
  stats: ItemStats;
  stats_base: ItemStats;
  stats_enhance: ItemStats;
  set_info?: ItemSetInfo | null;
  sell_gold: number;
  upgrade_gold_cost: number | null;
  upgrade_success_rate?: number | null;
  acquired_at: string;
  is_equipped: boolean;
  /** False when hero level is below template.item_level (server-side; grandfather if already equipped). */
  can_equip?: boolean;
}

export interface EquippedSlot {
  slot: ItemSlot;
  slot_label: string;
  item: PlayerItem | null;
}

export interface ConsumableItem {
  id: number;
  item_id: string;
  name: string;
  description: string;
  quantity: number;
  acquired_at: string;
}

export interface SocketGem {
  type: string;
  level: number;
}

export interface GemStack {
  gem_type: string;
  level: number;
  quantity: number;
  stats: Record<string, number>;
}

export interface MaterialStack {
  level: number;
  quantity: number;
}

export interface InventoryOverview {
  items: PlayerItem[];
  consumables: ConsumableItem[];
  gems?: GemStack[];
  shards?: MaterialStack[];
  legendary_stones?: MaterialStack[];
  equipped: EquippedSlot[];
  capacity: number;
  used: number;
  slot_upgrade_cost_diamonds: number;
  slot_upgrade_increment: number;
  socket_unlock_cost_diamonds?: number;
}

export type LevelableSkill =
  | "SKILL_1"
  | "SKILL_2"
  | "ULT"
  | "SKILL_4"
  | "SKILL_5"
  | "SKILL_6"
  | "SKILL_7"
  | "SKILL_8";

export type SkillActionType =
  | "ATTACK"
  | "AOE"
  | "HEAL"
  | "HEAL_AOE"
  | "STRIP_BUFFS"
  | "STRIP_DEBUFFS"
  | "SHIELD";

export interface SkillLevelInfo {
  level: number;
  required_hero_level: number;
  gold_cost: number;
  damage_mult: number;
  mana_cost: number;
  mana_gain: number;
  cooldown_sec?: number;
  action_type: SkillActionType;
  damage_type?: DamageType | null;
  description: string | null;
  unlocked: boolean;
  reason: string | null;
  max_level?: number;
}

export interface SkillBasicInfo {
  damage_mult: number;
  mana_gain: number;
  action_type: SkillActionType;
  damage_type?: DamageType | null;
  display_name?: string;
}

export interface SkillTreeEntry {
  skill_name: LevelableSkill;
  display_name: string;
  max_level: number;
  current_level: number;
  levels: SkillLevelInfo[];
}

export interface SkillTreeBranchMeta {
  branch: number;
  active_branch: number;
  branch_unlocked: boolean;
  branch_unlock_cost_diamonds: number;
  reset_cost_diamonds: number;
  sp_cost_per_upgrade: number;
  skill_points_earned: number;
  hero_max_level?: number;
  skill_points_effective_level?: number;
}

export interface SkillTreeResponse extends SkillTreeBranchMeta {
  skill_points: number;
  skills: SkillTreeEntry[];
  skill_names?: Record<string, string>;
}

export interface PassiveLevelInfo {
  level: number;
  required_hero_level: number;
  gold_cost: number;
  description: string | null;
  unlocked: boolean;
  reason: string | null;
}

export interface PassiveTreeEntry {
  passive_code: string;
  display_name: string;
  max_level: number;
  current_level: number;
  levels: PassiveLevelInfo[];
}

export interface PassiveTreeResponse extends SkillTreeBranchMeta {
  skill_points: number;
  passives: PassiveTreeEntry[];
}

export interface CampaignStage {
  id: number;
  order_index: number;
  tier: number;
  stage_number: number;
  name: string;
  min_hero_level: number;
  is_boss: boolean;
  reward_gold: number;
  reward_xp: number;
  reward_diamonds: number;
  unlocked: boolean;
  completed: boolean;
  times_completed: number;
}

export type CampaignRunStatus = "ACTIVE" | "COMPLETED" | "FAILED";

export interface CampaignStageNode {
  id: string;
  row: number;
  col: number;
  display_type: "battle" | "elite" | "final_boss" | "boss" | "treasure" | "mystery" | "dead_end" | string;
  cleared: boolean;
  reachable: boolean;
}

export interface CampaignRun {
  id: number;
  stage_id: number;
  stage_name: string;
  status: CampaignRunStatus;
  current_node_id: string | null;
  nodes: CampaignStageNode[];
  edges: { from: string; to: string }[];
  started_at: string;
  completed_at: string | null;
  info_messages?: string[];
}

export interface CampaignBalanceStatBlock {
  hp: number;
  atk: number;
  def_: number;
  spd: number;
  fixed_damage?: number;
}

export interface CampaignBalanceCodeSuggestion {
  code: string;
  name: string;
  current: CampaignBalanceStatBlock;
  fought_as: CampaignBalanceStatBlock;
  suggested: CampaignBalanceStatBlock;
}

export interface CampaignBalanceMetrics {
  turn_count: number;
  player_hp_pct_end: number;
  player_mana_end: number;
}

export interface CampaignBattleEnemyRow {
  code: string | null;
  name: string;
  hp: number;
  atk: number;
  def_: number;
  spd: number;
  fixed_damage?: number;
  formation: string;
  hero_class: string;
}

export interface CampaignBalanceMonsterOption {
  code: string;
  name: string;
  tier: number;
  hp: number;
  atk: number;
  def_: number;
  spd: number;
  fixed_damage: number;
  hero_class: string;
  formation: string;
}

export interface CampaignBalanceEnemySlotPayload {
  code?: string | null;
  clone_from_code?: string | null;
  name?: string | null;
  hp?: number | null;
  atk?: number | null;
  def_?: number | null;
  spd?: number | null;
  fixed_damage?: number | null;
  formation?: string | null;
  hero_class?: string | null;
}

export interface CampaignBattleBalancePayload {
  run_id?: number | null;
  stage_tier?: number | null;
  node_id: string;
  display_type: string;
  battle_result: "WIN" | "LOSS";
  enemies: CampaignBattleEnemyRow[];
  elite_multiplier: number;
  metrics: CampaignBalanceMetrics;
  summary: string;
  recommendation_notes: string[];
  suggestions: CampaignBalanceCodeSuggestion[];
}

export interface CampaignMoveResult {
  run_id: number;
  node_id: string;
  display_type: string;
  revealed_type?: string | null;
  battle_result: "WIN" | "LOSS" | null;
  rewards: { gold: number; xp: number; diamonds?: number } | null;
  stage_completed: boolean;
  log: BattleLogEvent[] | null;
  outcome_message: string;
  energy_lost: number;
  debuff_applied?: { stat?: string; pct?: number; label?: string; message?: string } | null;
  dropped_items?: PlayerItem[];
  info_messages?: string[];
  battle_balance?: CampaignBattleBalancePayload | null;
}
